import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { useDrop } from "react-dnd";
import { Pause, Play, SkipBack, SkipForward, X, Loader2 } from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";

import type { PlaybackFile } from "../../types";
import { PlaybackPlayer } from "../Player/PlaybackPlayer";
import { searchRecordings, startPlayback, stopPlayback } from "../../api/playback";
import { toLocalIso, playbackEndFor } from "../../lib/playbackTime";
import {
  transcodeKey as makeTranscodeKey, readTranscodeFlag, writeTranscodeFlag, clearTranscodeFlag,
} from "../../lib/transcodeFlag";
import { usePlaybackGridStore } from "../../store/playbackGridStore";

export interface PlaybackCellHandle {
  seek: (iso: string) => void;
  setPaused: (paused: boolean) => void;
  stop: () => void;
}

interface Props {
  cellId: string;
  deviceId: number | null;
  channel: number | null;
  channelName?: string | null;
  date: string; // yyyy-MM-dd
  rate: number;
  focused: boolean;
  showControls: boolean; // controles propios (modo independiente)
  onFocus: (cellId: string) => void;
  onFilesChange: (cellId: string, files: PlaybackFile[]) => void;
  onCursorChange: (cellId: string, iso: string | null) => void;
}

// Velocidad que se le pide al DVR para una velocidad de la UI. >1 (2x/4x/8x) se pide al
// servidor, que enruta el RTSP por el proxy local: inyecta Scale/Speed en el PLAY (el
// DVR manda N segundos grabados por segundo) y COMPRIME los timestamps RTP por N, asi
// el <video> reproduce a 1x y el cursor avanza xN (go2rtc no tolera deltas >1 s entre
// cuadros clave al muxear MSE). 0.5x y 1x se resuelven en el cliente (playbackRate)
// sobre el stream a tiempo real.
function serverSpeedFor(rate: number): number {
  return rate > 1 ? rate : 1;
}

// --- Fin del tramo pedido (fix del "loop" de Playback) ---------------------------
// Cuando el DVR llega a `endtime` cierra la sesion RTSP y go2rtc 1.9.x RECONECTA solo
// a la MISMA URL (mismo starttime): vuelve a mandar el tramo desde el inicio sin avisar
// al navegador (sin "ended", sin cerrar el WS, sin salto de timestamps: el <video>
// sigue avanzando). La celda detecta sola que el tramo ya se entrego completo,
// comparando la grabacion ya reproducida con la que contiene el tramo, y pasa al
// archivo siguiente (o corta el stream si no hay mas). Cerrar el WS corta la repeticion.
// Tramo que arranca a mitad de archivo: el DVR puede arrancar en el cuadro clave
// anterior (hasta ~1 GOP de mas), asi que se tolera 1 s extra antes de cortar.
const END_MARGIN_MS = 1000;
// Sin timeupdate por este tiempo (y sin pausa) = el <video> se quedo sin datos.
const STARVE_MS = 1500;
// ...si eso ocurre a menos de esto del fin del tramo (ms grabados; crece con la
// velocidad: cada timeupdate cubre N veces mas grabacion), es el fin real del tramo:
// cerrar ya, antes de que go2rtc empiece a repetirlo.
function nearEndMs(factor: number): number {
  return 2000 + 500 * factor;
}
// Stall largo del player a mitad del ULTIMO archivo: retomar desde el cursor solo si
// este arranque ya avanzo al menos esto (sin reintentos en bucle en un mismo punto).
const MIN_PROGRESS_MS = 3000;
// El archivo siguiente debe terminar al menos esto despues del fin del tramo
// (descarta duplicados dav/mp4 del mismo tramo con el fin corrido un segundo).
const NEXT_FILE_MIN_MS = 1000;

interface RangeGuard {
  seq: number;                  // launchSeqRef del arranque al que pertenece
  startMs: number;              // inicio pedido (= startedAt del cursor)
  endMs: number;                // fin pedido
  factor: number;               // velocidad efectiva del DVR (cursor = inicio + t*factor)
  files: PlaybackFile[] | null; // lista con la que se midieron lenMs/marginMs
  lenMs: number;                // grabacion que el DVR entrega para [inicio, fin]
  contentEndMs: number;         // hora grabada donde termina esa grabacion
  marginMs: number;             // tolerancia extra antes de cortar (preroll)
  lastElapsedMs: number;        // ultimo avance reportado (ms grabados)
  lastTuAt: number;             // performance.now() del ultimo timeupdate (0 = ninguno)
  fired: boolean;               // ya se actuo sobre este tramo
}

const isoMs = (iso: string): number => new Date(iso).getTime();

// Hoy: la lista de archivos se busca una vez al asignar el canal y el DVR sigue
// grabando. Al terminar lo ultimo de la lista se vuelve a listar (ver finishRange) y se
// sigue solo si hay grabacion nueva que termine al menos esto antes de "ahora".
const LIVE_EDGE_MS = 5000;

const todayDate = (): string => toLocalIso(new Date()).slice(0, 10);

// Archivos recortados a [s, e], ordenados y fusionados (la lista del DVR no viene
// ordenada ni deduplicada).
function mergedIntervals(files: PlaybackFile[], s: number, e: number): [number, number][] {
  const iv = files
    .map((f): [number, number] => [Math.max(isoMs(f.start), s), Math.min(isoMs(f.end), e)])
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  const out: [number, number][] = [];
  for (const [a, b] of iv) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) {
      if (b > last[1]) last[1] = b;
    } else {
      out.push([a, b]);
    }
  }
  return out;
}

// Grabacion contenida en [s, e]. go2rtc comprime a ~1 ms los saltos >1 s entre cuadros,
// asi que el <video> solo avanza por lo grabado.
function coveredMs(files: PlaybackFile[], s: number, e: number): number {
  return mergedIntervals(files, s, e).reduce((t, [a, b]) => t + (b - a), 0);
}

// Hora grabada a la que llega el stream tras reproducir `elapsedMs` de grabacion desde
// `s` (salta los huecos, como el DVR). Sin archivos conocidos: s + elapsedMs.
function recordedAt(files: PlaybackFile[], s: number, e: number, elapsedMs: number): number {
  let left = elapsedMs;
  let last = s + elapsedMs;
  for (const [a, b] of mergedIntervals(files, s, e)) {
    if (left < b - a) return a + left;
    left -= b - a;
    last = b;
  }
  return last;
}

// (Re)mide el tramo con la lista vigente: si se arranco mientras la busqueda del dia
// seguia en curso, se vuelve a medir cuando llega la lista.
function measureGuard(g: RangeGuard, files: PlaybackFile[]): void {
  if (g.files === files) return;
  g.files = files;
  // El DVR no puede entregar grabacion posterior a "ahora" (hoy, archivo en curso).
  const upTo = Math.min(g.endMs, Date.now());
  const covered = coveredMs(files, g.startMs, upTo);
  // Sin archivos conocidos en el tramo (busqueda en curso o fallida, u hoy despues
  // del ultimo archivo listado): el DVR entrega a lo sumo hasta "ahora".
  g.lenMs = covered > 0
    ? covered
    : (Math.max(upTo - g.startMs, 0) || g.endMs - g.startMs);
  g.contentEndMs = covered > 0
    ? recordedAt(files, g.startMs, upTo, covered)
    : g.startMs + g.lenMs;
  // Arranque a mitad de un archivo (no en su inicio ni en un hueco): posible preroll.
  g.marginMs = files.some((f) => isoMs(f.start) < g.startMs - 500 && isoMs(f.end) > g.startMs)
    ? END_MARGIN_MS
    : 0;
}

// Primer archivo (por hora de inicio) que todavia tiene grabacion despues de `afterMs`.
function nextFileAfter(files: PlaybackFile[], afterMs: number): PlaybackFile | null {
  let best: PlaybackFile | null = null;
  let bestStart = Infinity;
  for (const f of files) {
    const fs = isoMs(f.start);
    if (isoMs(f.end) > afterMs + NEXT_FILE_MIN_MS && fs < bestStart) {
      best = f;
      bestStart = fs;
    }
  }
  return best;
}

// Inicio del autoplay y de "Reproducir" sin cursor. La lista del dia trae archivos del dia
// anterior que terminan a las 00:00:00 o unos segundos despues (el backend deja pasar
// fin == inicio del dia) y no viene ordenada: primer archivo con grabacion dentro del dia,
// recortado a las 00:00:00. null = ningun archivo tiene grabacion en el dia.
function dayFirstStart(files: PlaybackFile[], date: string): string | null {
  const dayStart = `${date}T00:00:00`;
  const first = nextFileAfter(files, isoMs(dayStart));
  if (!first) return null;
  return isoMs(first.start) < isoMs(dayStart) ? dayStart : first.start;
}

export const PlaybackCell = forwardRef<PlaybackCellHandle, Props>(function PlaybackCell(
  {
    cellId, deviceId, channel, channelName, date, rate, focused, showControls,
    onFocus, onFilesChange, onCursorChange,
  },
  ref,
) {
  const assignChannel = usePlaybackGridStore((s) => s.assignChannel);
  const clearCell = usePlaybackGridStore((s) => s.clearCell);

  const [files, setFiles] = useState<PlaybackFile[]>([]);
  const [playbackId, setPlaybackId] = useState<string | null>(null);
  const [streamName, setStreamName] = useState<string | null>(null);
  const [cursorIso, setCursorIso] = useState<string | null>(null);
  const [startedAtIso, setStartedAtIso] = useState<string | null>(null);
  const [paused, setPausedState] = useState(false);
  const [searching, setSearching] = useState(false);
  // `starting`: negociando el playback (entre el pedido y el primer frame). Evita
  // mostrar un cartel "vacio" mientras arranca; el overlay muestra "Conectando...".
  const [starting, setStarting] = useState(false);
  // Velocidad EFECTIVA que el backend configuro en el DVR para el stream actual
  // (1 = tiempo real). Con N>1 el stream ya viene "comprimido" xN (ver arriba): el
  // <video> queda en 1x y el cursor avanza xN. Nunca aceleramos el <video> por
  // encima de lo que entrega el DVR (se vaciaria el buffer y congelaria).
  const [serverSpeed, setServerSpeed] = useState(1);
  // Clave del ultimo (device|channel|date) auto-reproducido, para auto-play una
  // sola vez por asignacion (no re-arrancar tras Detener).
  const autoPlayedRef = useRef<string>("");

  const transcodeKey = makeTranscodeKey(deviceId, channel);
  const [transcode, setTranscode] = useState<boolean>(() => readTranscodeFlag(transcodeKey));
  useEffect(() => { setTranscode(readTranscodeFlag(transcodeKey)); }, [transcodeKey]);

  // Refs para que los metodos imperativos / callbacks lean valores frescos.
  const filesRef = useRef(files); filesRef.current = files;
  const transcodeRef = useRef(transcode); transcodeRef.current = transcode;
  const cursorRef = useRef(cursorIso); cursorRef.current = cursorIso;
  const startedAtRef = useRef(startedAtIso); startedAtRef.current = startedAtIso;
  const playbackIdRef = useRef(playbackId); playbackIdRef.current = playbackId;
  const dateRef = useRef(date); dateRef.current = date;
  const rateRef = useRef(rate); rateRef.current = rate;
  const startingRef = useRef(starting); startingRef.current = starting;
  const serverSpeedRef = useRef(serverSpeed); serverSpeedRef.current = serverSpeed;
  // Si el stream de reproduccion rapida fallo, la celda vuelve a 1x hasta que el
  // usuario cambie de velocidad (se resetea al cambiar `rate`).
  const speedFallbackRef = useRef(false);
  // Velocidad PEDIDA por el arranque vigente (0 = ninguno). El efecto de cambio de
  // velocidad compara contra esto (no contra la efectiva del ultimo stream que
  // respondio) para relanzar bien aunque haya un arranque en vuelo o un valor
  // viejo tras Detener.
  const requestedSpeedRef = useRef(0);
  // El servidor/equipo no soporta velocidad rapida (respondio speed=1 a un
  // pedido >1): no relanzar en cada clic de 2x/4x/8x. Se resetea al cambiar de
  // (device, channel, date). Aviso al usuario una sola vez.
  const speedUnsupportedRef = useRef(false);
  const speedWarnedRef = useRef(false);
  // Secuencia de arranques: una respuesta de /playback/start que llega DESPUES de
  // un arranque mas nuevo (clics rapidos en la barra, cambio de velocidad) se
  // descarta y su stream se detiene, para no pisar el actual ni dejar refs colgados.
  const launchSeqRef = useRef(0);
  // Tramo del stream vigente (ver RangeGuard). null = ningun stream armado.
  const guardRef = useRef<RangeGuard | null>(null);
  // Arranque al que pertenece el <video> montado. Los callbacks del player se atan a
  // el para que un evento tardio del stream anterior no mueva el cursor ni cierre el
  // tramo nuevo. Cambia en el mismo render que streamName.
  const [streamSeq, setStreamSeq] = useState(0);
  const pausedRef = useRef(paused); pausedRef.current = paused;
  // La celda se detuvo sola al terminar las grabaciones de un dia pasado: "Reproducir"
  // vuelve a empezar desde el primer tramo en vez de pedir un tramo vacio.
  const dayEndedRef = useRef(false);
  // El detector de stall del player dispara una sola vez por stream; cambiar esto
  // (identidad de onStreamEnded) lo re-arma.
  const [stallArm, setStallArm] = useState(0);

  // Reportar files al padre (alimenta el timeline de la celda enfocada).
  useEffect(() => { onFilesChange(cellId, files); }, [files, cellId, onFilesChange]);
  // Reportar cursor SOLO si esta celda esta enfocada: el timeline compartido solo
  // muestra la enfocada, y con 8 celdas reproduciendo a ~4Hz reportar todas haria
  // re-render del padre decenas de veces por segundo. Al cambiar el foco, la nueva
  // celda re-reporta su cursor actual (focused esta en deps).
  useEffect(() => {
    if (focused) onCursorChange(cellId, cursorIso);
  }, [cursorIso, focused, cellId, onCursorChange]);

  // Drop target: arrastrar un canal a esta celda.
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: "CHANNEL",
      collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),
      drop: (item: any) => {
        if (item?.type === "CHANNEL") assignChannel(cellId, item.deviceId, item.channel);
      },
    }),
    [cellId, assignChannel],
  );

  // Detener el stream actual (si hay) e invalidar cualquier arranque en vuelo.
  const stopCurrent = useCallback(() => {
    launchSeqRef.current++;
    guardRef.current = null;
    dayEndedRef.current = false;
    requestedSpeedRef.current = 0;
    const id = playbackIdRef.current;
    if (id) stopPlayback(id).catch(() => {});
    setPlaybackId(null);
    setStreamName(null);
    setStarting(false);
  }, []);

  // Arrancar un playback en `iso`. Reusa el patron probado del single-cam: detener el
  // stream anterior (fire-and-forget) y abrir el nuevo acotando `end`. Pide al backend
  // la velocidad del DVR que corresponde a la velocidad elegida (o 1x si ya fallo).
  const launch = useCallback((iso: string, nextTranscode: boolean) => {
    if (deviceId == null || channel == null) return;
    const prevId = playbackIdRef.current;
    setPlaybackId(null);
    setStreamName(null);
    if (prevId) stopPlayback(prevId).catch(() => {});
    setStarting(true);
    const seq = ++launchSeqRef.current;
    guardRef.current = null;
    dayEndedRef.current = false;
    const dayEnd = toLocalIso(new Date(dateRef.current + "T23:59:59"));
    const end = playbackEndFor(iso, filesRef.current, dayEnd);
    const wanted = (speedFallbackRef.current || speedUnsupportedRef.current) ? 1 : serverSpeedFor(rateRef.current);
    requestedSpeedRef.current = wanted;
    startPlayback({ device_id: deviceId, channel, start: iso, end, transcode: nextTranscode, speed: wanted })
      .then((res) => {
        if (seq !== launchSeqRef.current) {
          // Llego tarde: ya hay un arranque mas nuevo (o la celda se detuvo). Liberar.
          stopPlayback(res.playback_id).catch(() => {});
          return;
        }
        const eff = typeof res.speed === "number" && res.speed > 0 ? res.speed : 1;
        setServerSpeed(eff);
        if (wanted > 1 && eff <= 1) {
          speedUnsupportedRef.current = true;
          if (!speedWarnedRef.current) {
            speedWarnedRef.current = true;
            toast(`CH${channel}: velocidad rapida no disponible en este equipo; reproduciendo a 1x`);
          }
        }
        const startMs = isoMs(iso);
        const endMs = isoMs(end);
        if (!isNaN(startMs) && !isNaN(endMs)) {
          const g: RangeGuard = {
            seq, startMs, endMs, factor: eff > 1 ? eff : 1, files: null,
            lenMs: 0, contentEndMs: 0, marginMs: 0, lastElapsedMs: 0, lastTuAt: 0, fired: false,
          };
          measureGuard(g, filesRef.current);
          guardRef.current = g;
        }
        setStreamSeq(seq);
        setPlaybackId(res.playback_id);
        setStreamName(res.stream_name);
      })
      .catch((e: any) => {
        if (seq !== launchSeqRef.current) return;
        toast.error(e?.response?.data?.detail || `CH${channel}: no se pudo iniciar`);
      })
      .finally(() => { if (seq === launchSeqRef.current) setStarting(false); });
  }, [deviceId, channel]);

  const doSeek = useCallback((iso: string) => {
    if (deviceId == null || channel == null) return;
    setCursorIso(iso);
    setStartedAtIso(iso);
    setPausedState(false);
    launch(iso, transcodeRef.current);
  }, [deviceId, channel, launch]);

  // Re-arrancar desde el cursor actual (fallbacks de transcode / de velocidad). El nuevo
  // stream empieza en el cursor, asi que `startedAt` se realinea a el.
  const reseek = useCallback((nextTranscode: boolean) => {
    const iso = cursorRef.current;
    if (!iso) {
      stopCurrent();
      return;
    }
    setStartedAtIso(iso);
    launch(iso, nextTranscode);
  }, [launch, stopCurrent]);

  // Buscar grabaciones del dia al cambiar (device, channel, date). Reset total.
  useEffect(() => {
    stopCurrent();
    setCursorIso(null);
    setStartedAtIso(null);
    setPausedState(false);
    setServerSpeed(1);
    speedFallbackRef.current = false;
    speedUnsupportedRef.current = false;
    speedWarnedRef.current = false;
    setFiles([]);
    autoPlayedRef.current = "";
    if (deviceId == null || channel == null) return;
    let cancelled = false;
    setSearching(true);
    const startD = new Date(date + "T00:00:00");
    const endD = new Date(date + "T23:59:59");
    searchRecordings({ device_id: deviceId, channel, start: toLocalIso(startD), end: toLocalIso(endD) })
      .then((res) => { if (!cancelled) setFiles(res.files); })
      .catch(() => { if (!cancelled) setFiles([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, channel, date]);

  // Auto-reproducir al cargar las grabaciones del dia: arranca desde el primer
  // tramo disponible DEL DIA (una sola vez por asignacion). Da feedback inmediato sin
  // pedir clic en Play. En Sincronizado, "Reproducir" arriba realinea todas a la
  // misma hora.
  useEffect(() => {
    if (deviceId == null || channel == null || searching) return;
    if (files.length === 0) return;
    if (playbackIdRef.current || starting) return;
    const key = `${deviceId}|${channel}|${date}`;
    if (autoPlayedRef.current === key) return;
    autoPlayedRef.current = key;
    const iso = dayFirstStart(files, date);
    if (iso) doSeek(iso);
  }, [files, searching, starting, deviceId, channel, date, doSeek]);

  // Cambio de velocidad (global desde la barra): si la velocidad del DVR que
  // corresponde difiere de la del stream actual y hay una reproduccion en curso,
  // re-arrancar desde el cursor con la nueva. 0.5x <-> 1x no toca el stream
  // (se resuelve en el <video>). Si esta detenida, la proxima reproduccion ya
  // usa la velocidad nueva.
  useEffect(() => {
    speedFallbackRef.current = false;
    const want = serverSpeedFor(rate);
    if (!(playbackIdRef.current || startingRef.current)) return;
    // Sin soporte del servidor/equipo: relanzar no cambiaria nada.
    if (want > 1 && speedUnsupportedRef.current) return;
    if (want === requestedSpeedRef.current) return;
    const iso = cursorRef.current;
    if (!iso) return;
    doSeek(iso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate]);

  // Cleanup al desmontar: detener el stream activo e invalidar un arranque en
  // vuelo (su respuesta tardia entra en la rama seq distinto y se detiene sola).
  useEffect(() => {
    return () => {
      launchSeqRef.current++;
      if (playbackIdRef.current) stopPlayback(playbackIdRef.current).catch(() => {});
    };
  }, []);

  useImperativeHandle(ref, () => ({
    seek: (iso: string) => doSeek(iso),
    setPaused: (p: boolean) => setPausedState(p),
    stop: () => stopCurrent(),
  }), [doSeek, stopCurrent]);

  // Cierre del tramo (el DVR ya lo entrego; go2rtc lo repetiria desde el inicio):
  // pasar al primer archivo con grabacion DESPUES del fin del tramo, o cortar el
  // stream si no hay mas. Salida unica de un tramo: la usan el chequeo por grabacion
  // reproducida, el de video sin datos cerca del fin y el stall largo del player.
  const finishRange = useCallback((g: RangeGuard) => {
    if (g.fired || guardRef.current !== g || g.seq !== launchSeqRef.current) return;
    g.fired = true;
    const next = nextFileAfter(filesRef.current, g.endMs);
    if (next) {
      // Contiguo o tras un hueco: desde su inicio. Solapado: desde el fin del tramo.
      doSeek(isoMs(next.start) >= g.endMs ? next.start : toLocalIso(new Date(g.endMs)));
    } else {
      // Sin mas grabaciones en la lista: cerrar el stream (corta la repeticion de go2rtc).
      // El cursor queda donde llego.
      stopCurrent();
      const day = dateRef.current;
      if (day !== todayDate()) {
        dayEndedRef.current = true;
        return;
      }
      // Hoy el DVR pudo seguir grabando despues de la busqueda. Con la lista vieja no se
      // puede medir lo que venga (el DVR salta los huecos y go2rtc repetiria el tramo):
      // volver a listar y seguir solo si la lista nueva muestra grabacion despues de lo
      // reproducido. El tramo nuevo se mide con esa lista. Nunca relanza en vacio.
      if (g.lastElapsedMs < MIN_PROGRESS_MS || deviceId == null || channel == null) return;
      const seq = launchSeqRef.current;
      const from = g.contentEndMs;
      setStarting(true);
      searchRecordings({ device_id: deviceId, channel, start: `${day}T00:00:00`, end: `${day}T23:59:59` })
        .then((res) => {
          if (seq !== launchSeqRef.current) return; // el usuario ya hizo otra cosa
          filesRef.current = res.files; // launch() la lee antes del proximo render
          setFiles(res.files);
          const nf = nextFileAfter(res.files, from);
          if (!nf) return;
          const target = Math.max(isoMs(nf.start), Math.ceil(from / 1000) * 1000);
          if (target < Date.now() - LIVE_EDGE_MS) doSeek(toLocalIso(new Date(target)));
        })
        .catch(() => {})
        .finally(() => { if (seq === launchSeqRef.current) setStarting(false); });
    }
  }, [deviceId, channel, doSeek, stopCurrent]);

  // Sincroniza el cursor del timeline con el avance real del video. `elapsed` es
  // video.currentTime. Con velocidad del DVR N>1 los timestamps vienen comprimidos
  // por N (el <video> corre a 1x), asi que cada segundo de video son N segundos
  // grabados. El cursor no pasa del fin del tramo; al reproducir toda la grabacion
  // del tramo (mas la tolerancia de preroll) se cierra el tramo.
  const handleTimeUpdate = useCallback((seq: number, elapsedSeconds: number) => {
    const g = guardRef.current;
    if (!g || g.seq !== seq || g.fired) return;
    measureGuard(g, filesRef.current);
    const elapsedMs = Math.max(0, elapsedSeconds) * g.factor * 1000;
    g.lastElapsedMs = elapsedMs;
    g.lastTuAt = performance.now();
    setCursorIso(toLocalIso(new Date(Math.min(g.startMs + elapsedMs, g.endMs))));
    if (elapsedMs >= g.lenMs + g.marginMs) finishRange(g);
  }, [finishRange]);
  const onTimeUpdate = useCallback(
    (elapsedSeconds: number) => handleTimeUpdate(streamSeq, elapsedSeconds),
    [handleTimeUpdate, streamSeq],
  );

  // Stall largo del player (>10 s sin avanzar).
  const onStreamEnded = useCallback(() => {
    const g = guardRef.current;
    if (!g || g.seq !== streamSeq || g.fired) return;
    measureGuard(g, filesRef.current);
    // Cerca del fin del tramo, o con archivo siguiente (como antes): cerrar el tramo.
    if (g.lastElapsedMs >= g.lenMs - nearEndMs(g.factor) || nextFileAfter(filesRef.current, g.endMs)) {
      finishRange(g);
      return;
    }
    // Corte a mitad del ULTIMO archivo: antes no se hacia nada y, al vencer el timeout
    // RTSP (30 s), go2rtc re-marcaba el tramo desde el inicio. Retomar con una sesion
    // nueva desde la hora grabada a la que llego el video (en un tramo que arranco en un
    // hueco el cursor va atrasado respecto del video).
    if (g.lastElapsedMs >= MIN_PROGRESS_MS && coveredMs(filesRef.current, g.startMs, g.endMs) > 0) {
      g.fired = true;
      doSeek(toLocalIso(new Date(recordedAt(filesRef.current, g.startMs, g.endMs, g.lastElapsedMs))));
      return;
    }
    // Sin avance suficiente: nada (como antes), pero re-armar el detector del player para
    // poder retomar si el corte se repite mas adelante en este mismo stream.
    setStallArm((n) => n + 1);
    // stallArm en deps: cambia la identidad del callback y eso re-arma el detector.
  }, [finishRange, doSeek, streamSeq, stallArm]);

  // Video sin datos cerca del fin del tramo = el DVR termino de entregarlo y go2rtc
  // esta reconectando para repetirlo: cerrar ya, antes de que se vea la repeticion.
  // Solo cuenta desde el primer timeupdate del stream montado; en pausa no corre.
  // Con la pestana oculta Chrome pausa el <video> sin audio: eso no es "sin datos". Al
  // volver se re-arma el reloj (el intervalo pudo quedar atrasado por el throttling).
  useEffect(() => {
    if (!streamName) return;
    const rearm = () => {
      const g = guardRef.current;
      if (g && g.seq === streamSeq && g.lastTuAt !== 0) g.lastTuAt = performance.now();
    };
    const id = window.setInterval(() => {
      const g = guardRef.current;
      if (!g || g.seq !== streamSeq || g.fired || g.lastTuAt === 0) return;
      const now = performance.now();
      if (pausedRef.current || document.hidden) { g.lastTuAt = now; return; }
      if (now - g.lastTuAt < STARVE_MS) return;
      measureGuard(g, filesRef.current);
      if (g.lastElapsedMs >= g.lenMs - nearEndMs(g.factor)) finishRange(g);
    }, 500);
    document.addEventListener("visibilitychange", rearm);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", rearm);
    };
  }, [streamName, streamSeq, finishRange]);

  const onDecodeError = () => {
    if (transcodeRef.current) return;
    writeTranscodeFlag(transcodeKey);
    setTranscode(true);
    reseek(true);
  };
  const onTranscodeFailed = () => {
    if (!transcodeRef.current) return;
    clearTranscodeFlag(transcodeKey);
    setTranscode(false);
    reseek(false);
  };
  // Error no manejado por los fallbacks de codec. Si el stream era de reproduccion
  // rapida (proxy/DVR no respondio, enlace saturado...), volver a 1x una vez y avisar.
  const onPlayerError = useCallback((msg: string) => {
    if (serverSpeedRef.current <= 1 || speedFallbackRef.current) return;
    speedFallbackRef.current = true;
    console.warn("[playback] stream rapido fallo, volviendo a 1x:", msg);
    toast.error(`CH${channel}: fallo la reproduccion rapida; se reintenta a velocidad normal`);
    reseek(transcodeRef.current);
  }, [channel, reseek]);

  // Controles propios (modo independiente): play desde cursor/primer tramo, pausa, +-10s.
  const playLocal = () => {
    const iso = (dayEndedRef.current ? null : cursorRef.current)
      ?? dayFirstStart(filesRef.current, dateRef.current)
      ?? (filesRef.current[0]?.start)
      ?? `${dateRef.current}T00:00:00`;
    doSeek(iso);
  };
  const stepBy = (delta: number) => {
    const base = cursorRef.current
      ? new Date(cursorRef.current)
      : new Date(`${dateRef.current}T00:00:00`);
    base.setSeconds(base.getSeconds() + delta);
    const dayStart = new Date(`${dateRef.current}T00:00:00`);
    const dayEnd = new Date(`${dateRef.current}T23:59:59`);
    if (base < dayStart) base.setTime(dayStart.getTime());
    if (base > dayEnd) base.setTime(dayEnd.getTime());
    doSeek(toLocalIso(base));
  };

  const handleRemove = () => {
    stopCurrent();
    clearCell(cellId);
  };

  const label = channel != null
    ? `CH${channel}${channelName ? ` · ${channelName}` : ""}`
    : "";

  const empty = deviceId == null || channel == null;

  // playbackRate del <video>: con velocidad del DVR (N>1) el stream ya viene con los
  // timestamps comprimidos por el proxy y se reproduce a 1x (el cursor avanza xN);
  // sin ella, la client-side (0.5x o 1x). Nunca >1 sobre un stream a tiempo real.
  const effectiveRate = serverSpeed > 1 ? 1 : Math.min(rate, 1);

  return (
    <div
      ref={drop as any}
      className={clsx(
        "relative w-full h-full bg-black border border-line overflow-hidden",
        isOver && canDrop && "ring-2 ring-accent",
        focused && !empty && "ring-2 ring-blue-500",
      )}
      onClick={() => onFocus(cellId)}
    >
      {empty ? (
        <div className="absolute inset-0 flex items-center justify-center text-muted text-xs pointer-events-none text-center px-2">
          Arrastra un canal aqui
        </div>
      ) : (
        <>
          {/* El reproductor se monta SOLO cuando hay stream: asi no aparece el
              overlay "Sin senal" del player superpuesto a nuestro estado. */}
          {streamName ? (
            <PlaybackPlayer
              streamName={streamName}
              label={serverSpeed > 1 ? `${label} · ${serverSpeed}x` : label}
              onDecodeError={transcode ? undefined : onDecodeError}
              onTranscodeFailed={transcode ? onTranscodeFailed : undefined}
              onError={onPlayerError}
              paused={paused}
              playbackRate={effectiveRate}
              onTimeUpdate={onTimeUpdate}
              onStreamEnded={onStreamEnded}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-2 pointer-events-none">
              <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[11px]">{label}</span>
              {starting ? (
                <span className="flex items-center gap-1 text-blue-200 text-[11px]"><Loader2 className="animate-spin" size={14} /> Conectando...</span>
              ) : searching ? (
                <span className="flex items-center gap-1 text-muted text-[11px]"><Loader2 className="animate-spin" size={14} /> Buscando grabaciones...</span>
              ) : files.length === 0 ? (
                <span className="text-muted text-[11px]">Sin grabaciones este dia</span>
              ) : (
                <span className="flex items-center gap-1 text-muted text-[11px]"><Play size={12} /> Pulsa Reproducir</span>
              )}
            </div>
          )}

          {/* Quitar canal */}
          <button
            className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded hover:bg-red-600/80 z-20"
            onClick={(e) => { e.stopPropagation(); handleRemove(); }}
            title="Quitar canal"
          >
            <X size={11} />
          </button>

          {/* Controles propios (modo independiente) */}
          {showControls && (
            <div
              className="absolute bottom-1 left-1 right-1 flex items-center justify-center gap-1 z-20"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="bg-black/60 text-white p-1 rounded hover:bg-black/80 disabled:opacity-40"
                onClick={() => stepBy(-10)}
                disabled={files.length === 0}
                title="Retroceder 10s"
              >
                <SkipBack size={12} />
              </button>
              {streamName && !paused ? (
                <button className="bg-black/60 text-white p-1 rounded hover:bg-black/80" onClick={() => setPausedState(true)} title="Pausa">
                  <Pause size={12} />
                </button>
              ) : streamName && paused ? (
                <button className="bg-accent text-white p-1 rounded" onClick={() => setPausedState(false)} title="Reanudar">
                  <Play size={12} />
                </button>
              ) : (
                <button className="bg-accent text-white p-1 rounded disabled:opacity-40" onClick={playLocal} disabled={files.length === 0} title="Reproducir">
                  <Play size={12} />
                </button>
              )}
              <button
                className="bg-black/60 text-white p-1 rounded hover:bg-black/80 disabled:opacity-40"
                onClick={() => stepBy(10)}
                disabled={files.length === 0}
                title="Avanzar 10s"
              >
                <SkipForward size={12} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});
